import { useState } from 'react';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { LoadingState, EmptyState } from '@/components/EmptyState';
import { ErrorBanner } from '@/components/ErrorBanner';
import { StatusBadge } from '@/components/StatusBadge';
import { RequirePermission } from '@/components/RequirePermission';
import { useLanguage, useT } from '@/i18n/LanguageContext';
import { formatDateTime, formatNumber } from '@/i18n/format';
import type { ChannelRow, CredentialStatus, SyncStatus } from '@/api/types';

/**
 * Marketplace Shared Foundation epic — the Office Integrations screen
 * (Part 3). Shows every connected marketplace channel with its connection
 * status, credential status (masked only — never the plaintext token,
 * never rendered anywhere on this page), and reconciliation worker sync
 * state, plus the actions the epic asks for: write/rotate a credential,
 * revoke a credential, and a "Sync now" manual trigger that just nudges
 * the same worker logic the scheduler uses (apps/api's POST .../sync
 * calls the identical locked pollConnectionOnce path).
 *
 * CHANNELS.CREDENTIALS.WRITE gates the write/rotate/revoke actions
 * (narrower than CHANNELS.READ, which is all that's needed to see this
 * page at all) — RequirePermission hides the controls a role can't use;
 * the server enforces the real boundary independently, same discipline as
 * every other screen in this app (see RequirePermission's own docstring).
 */
export function ChannelsListPage(): JSX.Element {
  const result = useApi(() => endpoints.channels(), []);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const t = useT();

  return (
    <div>
      <div className="page-header">
        <h1>{t('nav.integrations')}</h1>
      </div>
      <p className="disabled-note">{t('channels.credentialsHint')}</p>

      {result.status === 'loading' && <LoadingState />}
      {result.status === 'error' && <ErrorBanner error={result.error} onRetry={result.reload} />}
      {result.status === 'success' &&
        (result.data.channels.length === 0 ? (
          <EmptyState title={t('channels.emptyTitle')} />
        ) : (
          <div className="table-scroll"><table>
            <thead>
              <tr>
                <th>{t('inbounds.colSeller')}</th>
                <th>{t('channels.colMarketplace')}</th>
                <th>{t('channels.colConnection')}</th>
                <th>{t('channels.colCredential')}</th>
                <th>{t('channels.colSyncStatus')}</th>
                <th>{t('channels.colLastSuccess')}</th>
                <th>{t('channels.colLastError')}</th>
                <th>{t('channels.colNextSync')}</th>
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {result.data.channels.map((c) => (
                <ChannelRowView
                  key={c.id}
                  channel={c}
                  expanded={expandedId === c.id}
                  onToggle={() => setExpandedId(expandedId === c.id ? null : c.id)}
                  onChanged={result.reload}
                />
              ))}
            </tbody>
          </table></div>
        ))}
    </div>
  );
}

function ChannelRowView({
  channel,
  expanded,
  onToggle,
  onChanged,
}: {
  channel: ChannelRow;
  expanded: boolean;
  onToggle: () => void;
  onChanged: () => void;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<unknown>(null);
  const { t, locale } = useLanguage();

  async function syncNow(): Promise<void> {
    setBusy(true);
    setActionError(null);
    try {
      await endpoints.triggerChannelSync(channel.id);
      onChanged();
    } catch (err) {
      setActionError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <tr onClick={onToggle} style={{ cursor: 'pointer' }}>
        <td>{channel.ownerId}</td>
        <td>{channel.kind}</td>
        <td>
          <StatusBadge value={channel.enabled ? 'ACTIVE' : 'SUSPENDED'} />
          {' '}
          <StatusBadge value={channel.health} />
        </td>
        <td>
          {channel.credentialRevoked ? (
            <StatusBadge value="REVOKED" />
          ) : channel.credentialConfigured ? (
            <StatusBadge value="ACTIVE" />
          ) : (
            <span className="sub">{t('channels.notConfigured')}</span>
          )}
        </td>
        <td>
          <StatusBadge value={channel.syncStatus ?? 'NONE'} />
        </td>
        <td>{channel.lastSuccessAt ? formatDateTime(channel.lastSuccessAt, locale) : '—'}</td>
        <td>{channel.lastErrorAt ? formatDateTime(channel.lastErrorAt, locale) : '—'}</td>
        <td>{channel.nextPollAt ? formatDateTime(channel.nextPollAt, locale) : '—'}</td>
        <td onClick={(e) => e.stopPropagation()}>
          <RequirePermission permission="CHANNELS.WRITE">
            <button type="button" disabled={busy || channel.credentialRevoked || !channel.credentialConfigured} onClick={() => void syncNow()}>
              {t('channels.syncNow')}
            </button>
          </RequirePermission>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={9} style={{ background: 'var(--surface-2, #f7f7f8)' }}>
            {Boolean(actionError) && <ErrorBanner error={actionError} onRetry={() => setActionError(null)} />}
            <ChannelDetail channelId={channel.id} onChanged={onChanged} />
          </td>
        </tr>
      )}
    </>
  );
}

function ChannelDetail({ channelId, onChanged }: { channelId: string; onChanged: () => void }): JSX.Element {
  const credResult = useApi(() => endpoints.channelCredentials(channelId), [channelId]);
  const syncResult = useApi(() => endpoints.channelSync(channelId), [channelId]);
  const t = useT();

  return (
    <div style={{ padding: 12 }}>
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 260 }}>
          <h3 style={{ fontSize: 13, marginTop: 0 }}>{t('channels.credential')}</h3>
          {credResult.status === 'success' && <CredentialPanel channelId={channelId} credentials={credResult.data.credentials} onChanged={onChanged} />}
          {credResult.status === 'loading' && <LoadingState />}
        </div>
        <div style={{ minWidth: 260 }}>
          <h3 style={{ fontSize: 13, marginTop: 0 }}>{t('channels.syncDetail')}</h3>
          {syncResult.status === 'success' && <SyncPanel sync={syncResult.data.sync} />}
          {syncResult.status === 'loading' && <LoadingState />}
        </div>
      </div>
    </div>
  );
}

function SyncPanel({ sync }: { sync: SyncStatus }): JSX.Element {
  const { t, locale } = useLanguage();
  if (!sync) return <p className="sub">{t('channels.noSyncStateYet')}</p>;
  return (
    <dl className="kv">
      <dt>{t('common.status')}</dt>
      <dd>
        <StatusBadge value={sync.status} />
      </dd>
      <dt>{t('channels.consecutiveFailures')}</dt>
      <dd>{formatNumber(sync.consecutiveFailures, locale)}</dd>
      <dt>{t('channels.lastPolled')}</dt>
      <dd>{sync.lastPolledAt ? formatDateTime(sync.lastPolledAt, locale) : '—'}</dd>
      <dt>{t('channels.colLastError')}</dt>
      <dd>{sync.lastErrorMessage ?? '—'}</dd>
    </dl>
  );
}

const CREDENTIAL_TYPE = 'WB_MARKETPLACE_API_KEY';

function CredentialPanel({
  channelId,
  credentials,
  onChanged,
}: {
  channelId: string;
  credentials: readonly CredentialStatus[];
  onChanged: () => void;
}): JSX.Element {
  const existing = credentials[0] ?? null;
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const { t, locale } = useLanguage();

  async function write(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await endpoints.writeChannelCredential({ channelId, credentialType: CREDENTIAL_TYPE, plaintext: value });
      setValue('');
      onChanged();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function revoke(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await endpoints.revokeChannelCredential({ channelId, credentialType: CREDENTIAL_TYPE });
      onChanged();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      {existing ? (
        <dl className="kv">
          <dt>{t('channels.configured')}</dt>
          <dd>{existing.configured ? t('channels.yesWithHint', { hint: existing.maskedHint ?? '****' }) : t('common.no')}</dd>
          <dt>{t('channels.revoked')}</dt>
          <dd>{existing.revoked ? t('common.yes') : t('common.no')}</dd>
          <dt>{t('channels.lastRotated')}</dt>
          <dd>{existing.rotatedAt ? formatDateTime(existing.rotatedAt, locale) : t('channels.never')}</dd>
        </dl>
      ) : (
        <p className="sub">{t('channels.noCredentialSlot')}</p>
      )}

      {Boolean(error) && <ErrorBanner error={error} onRetry={() => setError(null)} />}

      <RequirePermission permission="CHANNELS.CREDENTIALS.WRITE" fallback={<p className="sub">{t('channels.roleCannotWrite')}</p>}>
        <div style={{ marginTop: 8 }}>
          <label htmlFor={`cred-${channelId}`} style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
            {existing?.configured ? t('channels.rotateCredential') : t('channels.setCredential')}
          </label>
          <input
            id={`cred-${channelId}`}
            type="password"
            autoComplete="off"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={t('channels.pasteApiToken')}
            style={{ width: '100%', maxWidth: 420 }}
          />
          <div className="form-actions" style={{ marginTop: 8 }}>
            <button type="button" disabled={busy || value.length === 0} onClick={() => void write()}>
              {existing?.configured ? t('channels.rotate') : t('common.save')}
            </button>
            {existing?.configured && !existing.revoked && (
              <button type="button" className="danger" disabled={busy} onClick={() => void revoke()}>
                {t('channels.revoke')}
              </button>
            )}
          </div>
          <p className="sub" style={{ marginTop: 4 }}>
            {t('channels.encryptedHint')}
          </p>
        </div>
      </RequirePermission>
    </div>
  );
}
